import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'accent';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-ink-muted dark:bg-night-sunken dark:text-slate-400',
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  danger: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  accent: 'bg-accent-50 text-accent-700 dark:bg-accent-900/40 dark:text-accent-200',
};

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-medium',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Shared status vocabulary, so a status always looks the same everywhere. */
const STATUS_TONES: Record<string, Tone> = {
  // Voucher lifecycle
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'brand',
  POSTED: 'success',
  REVERSED: 'danger',
  CANCELLED: 'danger',
  // Units
  AVAILABLE: 'success',
  HOLD: 'warning',
  BOOKED: 'brand',
  AGREEMENT: 'brand',
  REGISTERED: 'accent',
  POSSESSION: 'accent',
  SOLD: 'success',
  // Demands
  PENDING: 'neutral',
  RAISED: 'brand',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  OVERDUE: 'danger',
  WAIVED: 'neutral',
  // Documents / orders
  PENDING_APPROVAL: 'warning',
  PARTIALLY_RECEIVED: 'warning',
  RECEIVED: 'success',
  CLOSED: 'neutral',
  IN_PROGRESS: 'brand',
  COMPLETED: 'success',
  ACTIVE: 'success',
  // Reconciliation
  UNRECONCILED: 'warning',
  SUGGESTED: 'brand',
  RECONCILED: 'success',
  IGNORED: 'neutral',
  // Projects
  PLANNING: 'neutral',
  LAUNCHED: 'brand',
  UNDER_CONSTRUCTION: 'warning',
  HANDED_OVER: 'success',
  ON_HOLD: 'warning',
  REJECTED: 'danger',
};

export function StatusBadge({ status, className }: { status: string; className?: string }) {
  const label = status
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  return (
    <Badge tone={STATUS_TONES[status] ?? 'neutral'} className={className}>
      {label}
    </Badge>
  );
}
